import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Quote } from '../../models/quote.model';
import { Company } from '../../models/company.model';
import { CompanyQuotes } from '../../models/company.quotes.model';
import { authToken } from '../../scripts/authentication.script';
import { convertRequirementsNumbers } from '../../scripts/api.payload.script';

export class QuoteDA {
  static apiResponse: ApiResponse

  public static async getQuotes(
    id_company: string,
    interval: string,
    start_date: string,
    end_date: string,
    contact_id: string,
    status: string,
    expiration: string,
    sort_field: string,
    sort_mode: string,
    group_by: string,
    page: number = 1,
    limit: number = 20,
    search: string = '',
    all: boolean = false
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyQuoteList: CompanyQuotes | null = null
      let companyQuoteGroup: CompanyQuotes | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.quotes.list.data.json').then(response => { companyQuoteList = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/sale.quotes.group.data.json').then(response => { companyQuoteGroup = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyQuoteList!.id_company === id_company || companyQuoteGroup!.id_company === id_company) {
        if (group_by.length === 0) {
          companyQuoteList!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, status: status, expiration: expiration, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        } else {
          companyQuoteGroup!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, status: status, expiration: expiration, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: (group_by.length === 0) ? companyQuoteList : companyQuoteGroup
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let startOfDay: Date = new Date(new Date().setHours(0, 0, 0, 0))
      let endOfDay: Date = new Date(new Date().setHours(23, 59, 59, 999))
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes?interval=${interval}&start_date=${start_date}&end_date=${end_date}&id_contact=${contact_id}&status=${status}&expiration=${expiration}&sort_by=${sort_field}&sort_order=${sort_mode}&group_by=${group_by}&start_day=${startOfDay}&end_day=${endOfDay}${all ? '&all=true' : `&page=${page}&limit=${limit}`}${search ? `&search=${encodeURIComponent(search)}` : ''}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getQuote(
    id_company: string,
    id_quote: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let quote: Quote | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.quote.data.json').then(response => { quote = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (quote!.id === id_quote) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: quote
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes/${id_quote}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postQuote(
    id_company: string,
    customer: any,
    concerning: string,
    date: string,
    valid_until: string,
    currency: any,
    foreign_currency: any,
    treasury_account: any,
    language: string,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string }[] }[],
    signatures: string[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } },
    reference: string,
    dunning_policy_id?: string | null
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        concerning: concerning,
        signatures: signatures,
        company: company_detail,
        valid_until: valid_until,
        currency: currency,
        foreign_currency: foreign_currency,
        treasury_account: treasury_account,
        customer: customer,
        requirements: convertRequirementsNumbers(requirements),
        created_date: date,
        language: language,
        reference: reference
      }
      data.dunning_policy_id = dunning_policy_id ?? null

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putQuote(
    id_company: string,
    id_quote: string,
    concerning: string,
    date: string,
    valid_until: string,
    currency: any,
    foreign_currency: any,
    treasury_account: any,
    language: string,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string }[] }[],
    signatures: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let quote: Quote | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.quote.data.json').then(response => { quote = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (quote!.id === id_quote) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes/${id_quote}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        concerning: concerning,
        signatures: signatures,
        requirements: convertRequirementsNumbers(requirements),
        valid_until: valid_until,
        language: language,
        currency: currency,
        foreign_currency: foreign_currency,
        treasury_account: treasury_account,
        created_date: date
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putQuoteStatus(
    id_company: string,
    id_quote: string,
    new_status: string,
    comment?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let quote: Quote | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.quote.data.json').then(response => { quote = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (quote!.id === id_quote) {
        quote!.status = new_status

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: quote
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes/${id_quote}/status`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        status: new_status,
        ...(comment && { comment })
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async sendQuote(
    id_company: string,
    id_quote: string,
    method?: string,
    message?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let quote: Quote | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.quote.data.json').then(response => { quote = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (quote!.id === id_quote) {
        quote!.status = 'pending'

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: quote
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes/${id_quote}/send`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {}
      if (method) { data.method = method }
      if (message) { data.message = message }

      await axios.post(url, Object.keys(data).length > 0 ? data : null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async attachVoucher(id_company: string, id_quote: string, body: { id_document_inbox: string, role?: string }): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes/${id_quote}/vouchers`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, body, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async uploadVoucher(id_company: string, id_quote: string, file: File, role?: string): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes/${id_quote}/vouchers/upload`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', 'web')
      if (role) formData.append('role', role)
      let config: any = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }
      const response = await axios.post(url, formData, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteVoucher(id_company: string, id_quote: string, index: number): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/quotes/${id_quote}/vouchers/${index}`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }
}
